// components/ErrorBoundary.tsx
// Error boundary component to catch and display React errors gracefully

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Log the error to console (in production, send to error tracking service)
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error,
            errorInfo,
        });

        // TODO: In production, send error to logging service like Sentry or Firebase Crashlytics
        // Example: logErrorToService(error, errorInfo);
    }

    handleReset = (): void => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render(): ReactNode {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <div style={styles.container}>
                    <div style={styles.card}>
                        <div style={styles.icon}>⚠️</div>
                        <h1 style={styles.title}>Something went wrong</h1>
                        <p style={styles.message}>
                            We encountered an unexpected error. Please try refreshing the page.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Error Details (Development Only)</summary>
                                <div style={styles.errorContent}>
                                    <p style={styles.errorName}>{this.state.error.toString()}</p>
                                    {this.state.errorInfo && (
                                        <pre style={styles.stackTrace}>
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    )}
                                </div>
                            </details>
                        )}

                        <div style={styles.actions}>
                            <button onClick={this.handleReset} style={styles.resetButton}>
                                Try Again
                            </button>
                            <button onClick={() => window.location.href = '/'} style={styles.homeButton}>
                                Go to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Inline styles for error boundary
const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f7fafc',
        padding: '20px',
    },
    card: {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        textAlign: 'center' as const,
    },
    icon: {
        fontSize: '64px',
        marginBottom: '20px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 'bold' as const,
        color: '#2d3748',
        marginBottom: '12px',
    },
    message: {
        fontSize: '16px',
        color: '#718096',
        marginBottom: '24px',
        lineHeight: '1.6',
    },
    details: {
        marginTop: '24px',
        marginBottom: '24px',
        textAlign: 'left' as const,
        backgroundColor: '#f7fafc',
        borderRadius: '8px',
        padding: '16px',
    },
    summary: {
        cursor: 'pointer',
        fontWeight: 'bold' as const,
        color: '#4a5568',
        marginBottom: '12px',
    },
    errorContent: {
        marginTop: '12px',
    },
    errorName: {
        color: '#e53e3e',
        fontWeight: 'bold' as const,
        marginBottom: '8px',
    },
    stackTrace: {
        backgroundColor: '#2d3748',
        color: '#e2e8f0',
        padding: '12px',
        borderRadius: '4px',
        fontSize: '12px',
        overflow: 'auto',
        maxHeight: '200px',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        marginTop: '24px',
    },
    resetButton: {
        backgroundColor: '#667eea',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '16px',
        fontWeight: 'bold' as const,
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    homeButton: {
        backgroundColor: '#48bb78',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '16px',
        fontWeight: 'bold' as const,
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
};
